import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import type { PublicSignerSignatureValue } from '../publicSignerTypes';

export const TYPED_SIGNATURE_FONT_FAMILY = '"Allura", cursive';

const DRAWN_SIGNATURE_DATA_URL_PREFIX = 'data:image/png;base64,';
const MAX_DRAWN_SIGNATURE_DATA_URL_LENGTH = 1_000_000;
const TYPED_SIGNATURE_BASE_FONT_SIZE = 26;
const TYPED_SIGNATURE_MIN_FONT_SIZE = 4;
const TYPED_SIGNATURE_HORIZONTAL_PADDING = 3;
const TYPED_SIGNATURE_VERTICAL_PADDING = 1;
const TYPED_SIGNATURE_FONT_SIZE_STEP = 0.5;
const TYPED_SIGNATURE_FONT_LOAD_SPEC = '30px "Allura"';

// Mirror of the validator in publicSignerValidation. Local drafts always
// originate from canvas.toDataURL('image/png'), but a render-site guard
// here defends against any future code path that smuggles a non-data URL
// into a draft and would otherwise leak Referer: /sign/:token.
function isSafeDrawnSignatureUrl(value: string) {
  return (
    value.startsWith(DRAWN_SIGNATURE_DATA_URL_PREFIX) &&
    value.length <= MAX_DRAWN_SIGNATURE_DATA_URL_LENGTH
  );
}

export function getTypedSignaturePreviewFontSize({
  boxHeight,
  boxWidth,
  fontScale = 1,
  measureTextWidth = measureAlluraTextWidth,
  text
}: {
  boxHeight?: number;
  boxWidth?: number;
  fontScale?: number;
  measureTextWidth?: (text: string, fontSize: number) => number;
  text: string;
}) {
  const scale = Number.isFinite(fontScale) && fontScale > 0 ? fontScale : 1;
  const maxFontSize = Math.max(
    TYPED_SIGNATURE_MIN_FONT_SIZE,
    Math.min(
      TYPED_SIGNATURE_BASE_FONT_SIZE,
      (boxHeight ?? TYPED_SIGNATURE_BASE_FONT_SIZE) -
        TYPED_SIGNATURE_VERTICAL_PADDING * 2
    )
  );
  const maxWidth = Math.max(
    0,
    (boxWidth ?? Number.POSITIVE_INFINITY) -
      TYPED_SIGNATURE_HORIZONTAL_PADDING * 2
  );

  for (
    let fontSize = maxFontSize;
    fontSize >= TYPED_SIGNATURE_MIN_FONT_SIZE;
    fontSize -= TYPED_SIGNATURE_FONT_SIZE_STEP
  ) {
    if (measureTextWidth(text, fontSize * scale) <= maxWidth * scale) {
      return fontSize;
    }
  }

  return TYPED_SIGNATURE_MIN_FONT_SIZE;
}

export function PublicSignerSignaturePreview({
  boxHeight,
  boxWidth,
  fontScale = 1,
  placeholder,
  value
}: {
  boxHeight?: number;
  boxWidth?: number;
  fontScale?: number;
  placeholder: string;
  value: PublicSignerSignatureValue | null;
}) {
  useRerenderWhenAlluraFontLoads();
  const typedSignatureFontSize =
    value?.type === 'typed'
      ? getTypedSignaturePreviewFontSize({
          boxHeight,
          boxWidth,
          fontScale,
          text: value.typed_name
        })
      : TYPED_SIGNATURE_BASE_FONT_SIZE;

  if (!value) {
    return (
      <Typography
        noWrap
        sx={{ fontSize: 11, fontWeight: 700, maxWidth: '100%' }}
      >
        {placeholder}
      </Typography>
    );
  }

  if (value.type === 'drawn') {
    if (!isSafeDrawnSignatureUrl(value.drawn_image_data_url)) {
      return null;
    }
    return (
      <Box
        alt="Drawn signature"
        component="img"
        src={value.drawn_image_data_url}
        sx={{
          height: '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          width: '100%'
        }}
      />
    );
  }

  return (
    <Typography
      sx={{
        fontFamily: TYPED_SIGNATURE_FONT_FAMILY,
        fontSize: typedSignatureFontSize * fontScale,
        lineHeight: 1,
        maxWidth: '100%',
        overflow: 'visible',
        whiteSpace: 'nowrap'
      }}
    >
      {value.typed_name}
    </Typography>
  );
}

function useRerenderWhenAlluraFontLoads() {
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) {
      return;
    }

    let isCancelled = false;
    const fontSet = document.fonts;
    void Promise.all([
      fontSet.load(TYPED_SIGNATURE_FONT_LOAD_SPEC),
      fontSet.ready
    ])
      .then(() => {
        if (!isCancelled) {
          setVersion((current) => current + 1);
        }
      })
      .catch((error: unknown) => {
        console.warn('Allura font failed to load', error);
      });

    return () => {
      isCancelled = true;
    };
  }, []);
}

let cachedMeasureContext: CanvasRenderingContext2D | null = null;

function measureAlluraTextWidth(text: string, fontSize: number) {
  const context = getMeasureContext();
  if (!context) {
    return text.length * fontSize * 0.5;
  }

  context.font = `${fontSize}px ${TYPED_SIGNATURE_FONT_FAMILY}`;
  return context.measureText(text).width;
}

function getMeasureContext() {
  if (cachedMeasureContext) {
    return cachedMeasureContext;
  }
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const canvas = document.createElement('canvas');
    cachedMeasureContext = canvas.getContext('2d');
    return cachedMeasureContext;
  } catch {
    return null;
  }
}
